import { supabase } from '@/lib/supabase';
import type { FinanceTopic } from '@/lib/finance-topics';

/** What `finance-topic` returns. Ids are always ones from the list that was sent. */
export type TopicPick =
  | { kind: 'topic'; id: string }
  | { kind: 'ask'; ids: [string, string] }
  | { kind: 'same' }
  | { kind: 'none' };

/**
 * Ask the small model which topic on screen a question is about. Only the ids, labels and kinds go
 * up — never a brief. Any failure is `none`: the question then goes with the figures only.
 */
export async function pickFinanceTopic(
  organizationId: string,
  question: string,
  topics: FinanceTopic[],
  current: string | null,
  signal?: AbortSignal,
): Promise<TopicPick> {
  const { data, error } = await supabase.functions.invoke<{ pick: TopicPick }>('finance-topic', {
    body: {
      organizationId,
      question,
      current,
      topics: topics.map((t) => ({ id: t.id, label: t.label, kind: t.kind })),
    },
    signal,
  });
  if (error || !data?.pick) return { kind: 'none' };
  const ids = new Set(topics.map((t) => t.id));
  const p = data.pick;
  if (p.kind === 'topic') return ids.has(p.id) ? p : { kind: 'none' };
  if (p.kind === 'ask') return ids.has(p.ids[0]) && ids.has(p.ids[1]) ? p : { kind: 'none' };
  return p.kind === 'same' ? p : { kind: 'none' };
}
